import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, StateCard, Badge } from '@ampersandbrands/ui';
import { useStoreMe, isNotAvailable } from '@/lib/useStores';
import {
  useLoyalty,
  type SignupMonth,
  type TopCustomer,
  type LoyaltyPeriodActivity,
} from '@/lib/useLoyalty';
import { useHqStore } from '@/lib/useHqStore';

/**
 * Loyalty is a single company-wide L&P program (CounterPoint LOLLI_LOY): one
 * points balance per customer, earned and redeemed at any store. There is no
 * per-store program. This page therefore frames everything as THIS store's
 * activity on that shared program, not a store-owned program.
 */

const PROGRAM_NOTE =
  'Loyalty is one shared Lolli & Pops program. Customers earn and redeem points at any store. This page shows this store’s activity on that shared program.';

function StatTile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card className="p-4">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-candy-ink-muted dark:text-candy-dark-ink-muted">
        {label}
      </p>
      <p className="mt-1.5 text-[20px] font-semibold leading-none text-candy-ink dark:text-candy-dark-ink">
        {value}
      </p>
      {hint ? (
        <p className="mt-1.5 text-[11px] leading-tight text-candy-ink-muted dark:text-candy-dark-ink-muted">
          {hint}
        </p>
      ) : null}
    </Card>
  );
}

function fmtPct(rate: number | null): string {
  if (rate === null) return '—';
  return `${(rate * 100).toFixed(1)}%`;
}

/**
 * Store-attributed activity on the shared program (issue #410): points earned
 * and redeemed on tickets rung at this store, and the member-attach rate (share
 * of this store's tickets where a loyalty member was identified at checkout),
 * for the current calendar month or year.
 */
function ActivitySection({ activity }: { activity: LoyaltyPeriodActivity }) {
  const [period, setPeriod] = useState<'month' | 'year'>('month');
  const p = activity[period];

  return (
    <div className="mb-4">
      <div className="mb-2 flex items-center justify-between">
        <div>
          <p className="text-[13px] font-semibold text-candy-ink dark:text-candy-dark-ink">
            Points &amp; member attach rate
          </p>
          <p className="text-[11px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
            Earned and redeemed on tickets rung at this store
          </p>
        </div>
        <div
          className="inline-flex overflow-hidden rounded-lg border border-candy-line dark:border-candy-dark-line"
          role="group"
          aria-label="Activity period"
        >
          {(['month', 'year'] as const).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setPeriod(key)}
              aria-pressed={period === key}
              className={`px-3 py-1 text-[12px] font-medium ${
                period === key
                  ? 'bg-candy-pink text-white dark:bg-candy-pink-dark'
                  : 'bg-transparent text-candy-ink-muted dark:text-candy-dark-ink-muted'
              }`}
            >
              {key === 'month' ? 'This month' : 'This year'}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <StatTile label="Points earned" value={p.points_earned.toLocaleString('en-US')} hint="At this store" />
        <StatTile label="Points redeemed" value={p.points_redeemed.toLocaleString('en-US')} hint="At this store" />
        <StatTile
          label="Member attach rate"
          value={fmtPct(p.attach_rate)}
          hint={`${p.member_tickets.toLocaleString('en-US')} of ${p.total_tickets.toLocaleString('en-US')} tickets`}
        />
      </div>
    </div>
  );
}

function SignupsChart({ months }: { months: SignupMonth[] }) {
  const [hovered, setHovered] = useState<number | null>(null);

  const maxSignups = Math.max(...months.map((m) => m.signups), 1);
  const W = 300;
  const H = 100;
  const padTop = 24;
  const chartH = H - padTop;
  const n = months.length;
  const barW = Math.floor((W - (n - 1) * 2) / n);

  function fmtMonth(iso: string): string {
    const [year, month] = iso.split('-');
    return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString('en-US', {
      month: 'short',
      year: '2-digit',
    });
  }

  return (
    <Card className="p-4">
      <p className="mb-1 text-[13px] font-semibold text-candy-ink dark:text-candy-dark-ink">
        New Members per Month
      </p>
      <p className="mb-3 text-[11px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
        Customers who enrolled at this store
      </p>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full"
        aria-label="New members enrolled per month at this store"
        role="img"
      >
        {months.map((m, i) => {
          const x = i * (barW + 2);
          const bh = Math.max(2, (m.signups / maxSignups) * chartH);
          const y = padTop + chartH - bh;
          const isHov = hovered === i;

          return (
            <g key={m.month}>
              <rect
                x={x}
                y={y}
                width={barW}
                height={bh}
                rx={2}
                fill={isHov ? '#E85D9E' : '#F5C0D9'}
                className="cursor-pointer"
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => setHovered(hovered === i ? null : i)}
              />
              {isHov && (
                <text
                  x={x + barW / 2}
                  y={Math.max(y - 4, 10)}
                  textAnchor="middle"
                  fontSize={9}
                  className="fill-candy-ink-muted dark:fill-candy-dark-ink-muted"
                >
                  {m.signups.toLocaleString('en-US')}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <div className="mt-1 flex justify-between text-[11px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
        <span>{months[0] ? fmtMonth(months[0].month) : ''}</span>
        <span>{months.at(-1) ? fmtMonth(months.at(-1)!.month) : ''}</span>
      </div>
    </Card>
  );
}

function TopMembersList({ customers }: { customers: TopCustomer[] }) {
  return (
    <Card padded={false}>
      <div className="flex items-start justify-between gap-3 border-b border-candy-line px-4 py-3 dark:border-candy-dark-line">
        <div>
          <p className="text-[13px] font-semibold text-candy-ink dark:text-candy-dark-ink">
            Top Members Enrolled Here
          </p>
          <p className="text-[11px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
            Lifetime points balance
          </p>
        </div>
        <Badge tone="info">Company-wide balance</Badge>
      </div>
      <ul>
        {customers.map((c, i) => (
          <li
            key={c.rank}
            className={`flex items-center gap-3 px-4 py-3 ${
              i < customers.length - 1
                ? 'border-b border-candy-line dark:border-candy-dark-line'
                : ''
            }`}
          >
            <span className="w-5 text-center text-[13px] font-semibold text-candy-ink-muted dark:text-candy-dark-ink-muted">
              {c.rank}
            </span>
            <span className="flex-1 text-[14px] text-candy-ink dark:text-candy-dark-ink">
              {c.display_name}
            </span>
            <span className="text-[13px] font-medium text-candy-ink-muted dark:text-candy-dark-ink-muted">
              {c.points_balance.toLocaleString('en-US')} pts
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function Loyalty() {
  const [params] = useSearchParams();
  const { storeNo: hqStoreNo } = useHqStore();
  const storeNo = params.get('store_no') ? Number(params.get('store_no')) : (hqStoreNo ?? undefined);

  const meQ = useStoreMe(storeNo);
  const loyaltyQ = useLoyalty(storeNo);

  const storeName = meQ.data?.store?.store_name;

  const pageHead = (
    <div className="mb-4">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-candy-pink dark:text-candy-pink-dark">
        Lolli &amp; Pops loyalty
      </p>
      <h1 className="text-2xl font-semibold text-candy-ink dark:text-candy-dark-ink">Loyalty</h1>
      <p className="text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">
        {storeName ? (
          <>
            This store&rsquo;s activity on the shared program &middot;{' '}
            <span className="font-medium">{storeName}</span>
          </>
        ) : (
          'This store’s activity on the shared program'
        )}
      </p>
    </div>
  );

  if (meQ.isLoading || loyaltyQ.isLoading) {
    return (
      <div className="mx-auto w-full max-w-lg pb-24">
        {pageHead}
        <div className="space-y-4">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-24 animate-pulse rounded-xl bg-candy-cream-warm dark:bg-candy-dark-surface-warm"
            />
          ))}
        </div>
      </div>
    );
  }

  if (loyaltyQ.error && isNotAvailable(loyaltyQ.error)) {
    return (
      <div className="mx-auto w-full max-w-lg pb-24">
        {pageHead}
        <StateCard
          dashed
          title="Loyalty activity isn't available for this store yet"
          description="This store's activity on the shared Lolli & Pops loyalty program will appear here once the nightly sync has data for it."
        />
      </div>
    );
  }

  const loadErr = loyaltyQ.error ?? meQ.error;
  if (loadErr) {
    return (
      <div className="mx-auto w-full max-w-lg pb-24">
        {pageHead}
        <StateCard
          tone="error"
          title="Could not load loyalty activity"
          description="Please try again in a moment."
        />
      </div>
    );
  }

  const data = loyaltyQ.data!;
  const currentYear = String(new Date().getFullYear());
  const currentMonth = `${currentYear}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  const newThisYear = data.signups_monthly
    .filter((m) => m.month.startsWith(currentYear))
    .reduce((sum, m) => sum + m.signups, 0);
  const newThisMonth =
    data.signups_monthly.find((m) => m.month === currentMonth)?.signups ?? 0;

  const hasActivity =
    data.summary.members_total > 0 ||
    data.signups_monthly.some((m) => m.signups > 0) ||
    data.top_customers.length > 0 ||
    (data.period_activity?.year.total_tickets ?? 0) > 0;

  return (
    <div className="mx-auto w-full max-w-lg pb-24">
      {pageHead}

      <Card className="mb-4 p-4">
        <p className="text-[12px] leading-snug text-candy-ink-muted dark:text-candy-dark-ink-muted">
          {PROGRAM_NOTE}
        </p>
      </Card>

      {!hasActivity ? (
        <StateCard
          dashed
          title="No loyalty activity recorded for this store yet"
          description="New members enrolled here and this store's members will appear once the nightly sync has data for it."
        />
      ) : (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3">
            <StatTile
              label="New members this month"
              value={newThisMonth.toLocaleString('en-US')}
              hint="Enrolled at this store"
            />
            <StatTile
              label="New members this year"
              value={newThisYear.toLocaleString('en-US')}
              hint="Enrolled at this store"
            />
            <StatTile
              label="Members enrolled here"
              value={data.summary.members_total.toLocaleString('en-US')}
              hint="Home store on the shared program"
            />
            <StatTile
              label="Active members (12 mo)"
              value={data.summary.members_active_12m.toLocaleString('en-US')}
              hint="Purchased anywhere in 12 months"
            />
          </div>

          {data.period_activity && <ActivitySection activity={data.period_activity} />}

          {data.signups_monthly.some((m) => m.signups > 0) && (
            <div className="mb-4">
              <SignupsChart months={data.signups_monthly} />
            </div>
          )}

          {data.top_customers.length > 0 && (
            <TopMembersList customers={data.top_customers} />
          )}
        </>
      )}
    </div>
  );
}
